import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  getPlaybackSourceFacts,
  PlaybackSource,
} from "../utils/playbackSourcePresentation";

const STORAGE_KEY = "jellyflix.native.player-source-preferences.v1";
const HEALTH_STORAGE_KEY = "jellyflix.native.player-source-health.v1";
const HEALTH_HALF_LIFE_MS = 7 * 24 * 60 * 60 * 1000;
const MIN_HEALTH_SAMPLES = 5;

export type SourcePreference = {
  preferredFingerprint?: string;
  ignoredFingerprints: string[];
};

type StoredSourcePreferences = Record<string, SourcePreference>;
export type SourceHealth = {
  successes: number;
  failures: number;
  updatedAt: number;
};
export type StoredSourceHealth = Record<string, SourceHealth>;

export type RankedPlaybackSource = {
  source: PlaybackSource;
  originalIndex: number;
  fingerprint: string;
  score: number;
  rank: number;
  pinned: boolean;
  ignored: boolean;
  languageTier: number;
  startupTier: number;
  healthAdjustment: number;
  preprobed: boolean;
  reasons: string[];
};

export type PlaybackSourceScoreTone = "strong" | "neutral" | "warning" | "danger";

export function getPlaybackSourceScoreTone(
  source: Pick<RankedPlaybackSource, "score" | "pinned" | "ignored">,
): PlaybackSourceScoreTone {
  if (source.pinned && !source.ignored) return "strong";
  if (source.ignored) return "danger";
  if (source.score >= 80) return "strong";
  if (source.score >= 55) return "neutral";
  if (source.score >= 25) return "warning";
  return "danger";
}

export const emptySourcePreference: SourcePreference = { ignoredFingerprints: [] };

export function getPlaybackSourceFingerprint(source: PlaybackSource) {
  const facts = getPlaybackSourceFacts(source);
  return [
    facts.quality,
    facts.releaseType,
    facts.codec,
    facts.visual,
    facts.languages.join("+"),
    facts.audioCodec,
    facts.layout,
    facts.container,
    facts.provider,
  ].map((value) => value || "-").join("|").toLowerCase();
}

export function rankPlaybackSources(
  sources: PlaybackSource[],
  preference: SourcePreference,
  preferredAudioLanguage = "",
  health: StoredSourceHealth = {},
  preprobedSourceIds: ReadonlySet<string> = new Set(),
): RankedPlaybackSource[] {
  const preferredLanguage = preferredAudioLanguage.trim().slice(0, 2).toUpperCase();
  const rows = sources.map((source, originalIndex) => {
    const facts = getPlaybackSourceFacts(source);
    const fingerprint = getPlaybackSourceFingerprint(source);
    const pinned = preference.preferredFingerprint === fingerprint;
    const ignored = preference.ignoredFingerprints.includes(fingerprint);
    const preprobed = source.Remux?.PreProbed === true
      || Boolean(source.Id && preprobedSourceIds.has(source.Id));
    const reasons: string[] = [];
    let score = 30;
    const direct = source.SupportsDirectPlay === true;
    const languageTier = !preferredLanguage
      ? 0
      : facts.languages.includes(preferredLanguage)
        ? 0
        : facts.languages.length === 0
          ? 1
          : 2;
    const startupTier = facts.quality === "4K"
      ? 6
      : direct && facts.quality === "FHD"
        ? 0
        : direct && facts.quality === "HD"
          ? 1
          : !direct && facts.quality === "FHD"
            ? 2
            : !direct && facts.quality === "HD"
              ? 3
              : direct
                ? 4
                : 5;

    // Automatic selection is deliberately startup-first. Probe-backed
    // PlaybackInfo makes these flags authoritative for the current device,
    // so a direct-play source can skip ffmpeg entirely. Within that safe
    // path, favour practical 1080p/720p encodes and leave large 4K/remux
    // versions available for an explicit user choice.
    if (source.SupportsDirectPlay) { score += 24; reasons.push("Direct play"); }
    else if (source.SupportsDirectStream) { score += 10; reasons.push("Direct stream"); }
    if (facts.quality === "FHD") { score += 16; reasons.push("Fast 1080p"); }
    else if (facts.quality === "HD") { score += 11; reasons.push("Fast 720p"); }
    else if (facts.quality === "4K") { score -= 18; reasons.push("4K opt-in"); }
    if (facts.codec === "H264") { score += 7; reasons.push("Compatible video"); }
    else if (facts.codec === "HEVC") { score += 5; reasons.push("Efficient video"); }
    if (facts.audioCodec) { score += 2; reasons.push(facts.audioCodec); }
    if (preferredLanguage && languageTier === 0) {
      score += 14;
      reasons.push(`${preferredLanguage} audio`);
    } else if (preferredLanguage && languageTier === 2) {
      score -= 12;
      reasons.push(`No ${preferredLanguage} audio`);
    }
    if (facts.bitrateMbps >= 4 && facts.bitrateMbps <= 16) {
      score += 8;
      reasons.push("Medium bitrate");
    } else if (facts.bitrateMbps >= 2 && facts.bitrateMbps <= 24) {
      score += 6;
    } else if (facts.bitrateMbps > 60) {
      score -= 20;
      reasons.push("Very high bitrate");
    } else if (facts.bitrateMbps > 30) {
      score -= 11;
      reasons.push("High bitrate");
    }
    if (preprobed) {
      score += 24;
      reasons.unshift("Ready now");
    }
    if (pinned) { score += 18; reasons.unshift("Pinned"); }
    if (ignored) { score -= 70; reasons.unshift("Ignored"); }
    const historical = health[fingerprint];
    const ageMs = historical
      ? Math.max(0, Date.now() - historical.updatedAt)
      : 0;
    const decay = historical
      ? Math.pow(0.5, ageMs / HEALTH_HALF_LIFE_MS)
      : 0;
    const successes = (historical?.successes ?? 0) * decay;
    const failures = (historical?.failures ?? 0) * decay;
    const samples = successes + failures;
    const healthAdjustment = samples >= MIN_HEALTH_SAMPLES
      ? Math.max(-8, Math.min(8, Math.round(((successes / samples) - 0.5) * 16)))
      : 0;
    if (healthAdjustment >= 3) reasons.push("Reliable source");
    else if (healthAdjustment <= -3) reasons.push("Recent failures");
    score += healthAdjustment;

    return {
      source,
      originalIndex,
      fingerprint,
      score: Math.max(0, Math.min(100, score)),
      rank: 0,
      pinned,
      ignored,
      languageTier,
      startupTier,
      healthAdjustment,
      preprobed,
      reasons: reasons.slice(0, 3),
    };
  });

  return rows
    .sort((left, right) => {
      if (left.ignored !== right.ignored) return Number(left.ignored) - Number(right.ignored);
      if (left.pinned !== right.pinned) return Number(right.pinned) - Number(left.pinned);
      if (left.languageTier !== right.languageTier) return left.languageTier - right.languageTier;
      if (left.preprobed !== right.preprobed) return Number(right.preprobed) - Number(left.preprobed);
      if (left.startupTier !== right.startupTier) {
        return left.startupTier - right.startupTier;
      }
      return right.score - left.score || left.originalIndex - right.originalIndex;
    })
    .map((row, index) => ({ ...row, rank: index + 1 }));
}

export async function readSourcePreference(seriesId?: string): Promise<SourcePreference> {
  if (!seriesId) return emptySourcePreference;
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    const stored = raw ? JSON.parse(raw) as StoredSourcePreferences : {};
    const value = stored[seriesId];
    return value ? { ...emptySourcePreference, ...value } : emptySourcePreference;
  } catch {
    return emptySourcePreference;
  }
}

export async function saveSourcePreference(seriesId: string, preference: SourcePreference) {
  const raw = await AsyncStorage.getItem(STORAGE_KEY);
  const stored = raw ? JSON.parse(raw) as StoredSourcePreferences : {};
  stored[seriesId] = preference;
  await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

export async function clearAllSourcePreferences() {
  await Promise.all([
    AsyncStorage.removeItem(STORAGE_KEY),
    AsyncStorage.removeItem(HEALTH_STORAGE_KEY),
  ]);
}

export async function readSourceHealth(): Promise<StoredSourceHealth> {
  try {
    const raw = await AsyncStorage.getItem(HEALTH_STORAGE_KEY);
    return raw ? JSON.parse(raw) as StoredSourceHealth : {};
  } catch {
    return {};
  }
}

export async function saveSourceOutcome(
  fingerprint: string,
  outcome: "success" | "failure",
): Promise<StoredSourceHealth> {
  const stored = await readSourceHealth();
  const now = Date.now();
  const previous = stored[fingerprint];
  const decay = previous
    ? Math.pow(0.5, Math.max(0, now - previous.updatedAt) / HEALTH_HALF_LIFE_MS)
    : 0;
  stored[fingerprint] = {
    successes: (previous?.successes ?? 0) * decay + (outcome === "success" ? 1 : 0),
    failures: (previous?.failures ?? 0) * decay + (outcome === "failure" ? 1 : 0),
    updatedAt: now,
  };
  await AsyncStorage.setItem(HEALTH_STORAGE_KEY, JSON.stringify(stored));
  return stored;
}
